import { forwardRef, Fragment } from 'react'
import styled from 'styled-components'

import { colors, transitions } from '@/styles'
import toId from '@/utils/to-id'

import Label, { active } from './floating-label'
import CheckedIcon from './checked.svg'
import RadioIcon from './radio.svg'

const Control = styled.input<{
  left?: number
  size?: number
  hasError?: boolean
}>`
  filter: none;
  height: 60px;
  border-radius: 6px;
  border: 1px solid ${colors.grey};
  padding: 0 15px;
  font-size: 14px;
  outline: none;
  width: 100%;
  max-width: 100%;
  transition: border-color ${transitions.ease};

  &:disabled,
  &[disabled] {
    cursor: default;
    opacity: 0.7;
    &:not([type]),
    &[type='email'],
    &[type=''] {
      cursor: not-allowed;
    }
  }

  &:not([type]),
  &[type=''],
  &[type='text'],
  &[type='code'],
  &[type='tel'],
  &[type='password'],
  &[type='email'] {
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      transition: background-color 5000s ease-in-out 0s;
      &:not([type='tel']) {
        -webkit-box-shadow: 0 0 0 50px ${colors.white} inset;
      }
    }
    &::placeholder {
      opacity: 0;
    }
  }

  &:not([type]),
  &[type=''],
  &[type='text'],
  &[type='code'],
  &[type='password'],
  &[type='email'] {
    padding: 12px 12px 0 calc(12px + ${props => props.left ?? 0}px);
    background: ${props => (props.disabled ? colors.grey96 : colors.white)};
    &:focus {
      border-color: ${colors.secondary};
    }
    ${props =>
      props.hasError &&
      `
      border-color: ${colors.red} !important;
    `}
  }

  &[type='checkbox'],
  &[type='radio'],
  &[type='submit'] {
    cursor: pointer;
  }

  &[type='checkbox'] {
    transition: background-color 0.1s ease-out 0s;
    height: 20px;
    width: 20px;
    min-width: 20px;
    padding: 0;
    appearance: none;
    border-color: ${colors.grey80};
    border-width: 1px;
    border-radius: 4px;
    background-color: ${colors.white};
  }

  &[type='radio'] {
    height: 16px;
    width: 16px;
    min-width: 16px;
    border-radius: 50%;
    border-color: ${colors.black};
    border-width: 1.5px;
    background-color: none;
    appearance: none;
  }

  &[type='radio']:checked {
    background-color: ${colors.white};
    background-image: url('${RadioIcon}');
    background-size: 14px;
    background-position: center;
    background-repeat: no-repeat;
    &:disabled {
      opacity: 0.3;
    }
  }

  &[type='checkbox']:checked {
    background-color: ${colors.red64};
    border-color: ${colors.red64};
    background-image: url('${CheckedIcon}');
    background-size: 12.5px auto;
    background-position: center center;
    background-repeat: no-repeat;

    &:disabled {
      opacity: 0.3;
    }
  }
`

const Container = styled.div`
  position: relative;
  input:focus + label {
    ${active}
  }
  input:not(:placeholder-shown) + label {
    ${active}
  }
  &:-webkit-autofill + label,
  &:-webkit-autofill:hover + label,
  &:-webkit-autofill:focus + label {
    ${active}
  }
`

const Required = styled.span`
  color: ${colors.red64};
`

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  hasError?: boolean
  left?: number
  size?: number
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      placeholder,
      prefix,
      id: idInitial,
      required,
      disabled,
      hasError,
      left,
      ...rest
    },
    ref
  ) => {
    const Outer = placeholder ? Container : Fragment
    const id = idInitial || toId(placeholder, prefix)
    const placeholderText = `${placeholder}`
    const labelProps = left ? { x: left + 2 } : {}
    const fontSize = 14
    return (
      <Outer>
        <Control
          type="text"
          {...rest}
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          left={left}
          ref={ref}
          hasError={hasError}
        />
        {placeholder && (
          <Label
            hasError={hasError}
            $disabled={disabled}
            htmlFor={id}
            size={fontSize}
            {...labelProps}
          >
            {placeholderText}
            {required && <Required>*</Required>}
          </Label>
        )}
      </Outer>
    )
  }
)

Input.displayName = 'Input'

export default Input
